import {
  Component,
  Injectable,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { OfflineReason, OfflineService } from 'app/services/offline.service';
import { debounce, flatMap } from 'lodash';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { User } from 'app/classes/user';
import { DeleteConfirmationDialogComponent } from 'app/components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { InitializeLoadScreenComponent } from 'app/components/initialize-load-screen/initialize-load-screen.component';
import { ManageUserDialogComponent } from 'app/components/manage-user-dialog/manage-user-dialog.component';
import { UserManagementService } from 'app/core/services/user-management.service';
import { UserSearchService } from 'app/core/services/user-search.service';
import { AuthService } from 'app/services/auth.service';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';

export interface UserElement {
  uuid: string;
  avatar: string;
  firstName: string;
  lastName: string;
  rank: string;
  roles: string;
}

export interface UserRole {
  uuid: string;
  name: string;
  description: string;
  is_admin: boolean;
  permissions: Array<any>;
}

@Component({
  selector: 'manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.scss'],
})
export class ManageUsersComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  isLoading = false;
  displayedColumns: string[] = [
    'lastName',
    'firstName',
    'email',
    'rank',
    'roles',
    'userMenu',
    'approved',
    'verified',
  ];
  pageNumber = 1;
  limitPerPage = 15;
  totalPages = 0;
  totalUsers = 0;
  sortField = 'lastname';
  sortOrder = 'asc';
  userSearchTerm = '';

  allUsers: User[] = [];
  dataSource: MatTableDataSource<User> = new MatTableDataSource<User>(
    this.allUsers
  );
  offlineStatusSubscription: Subscription = new Subscription();
  isOnline: boolean;
  navigatorOnline: boolean;
  currentUser: User;

  private debouncedSortData = debounce(this.sortData.bind(this), 500);
  private debouncedFetchUsers = debounce(this.fetchUsers.bind(this), 500);

  constructor(
    private service: UserSearchService,
    private userManagementService: UserManagementService,
    private dialog: MatDialog,
    private offline: OfflineService,
    private snackbar: MatSnackBar,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.fetchUsers(this.userSearchTerm);
    this.offlineStatusSubscription = this.offline.status.subscribe(value => {
      this.isOnline = value;
      this.navigatorOnline = this.offline.isNavigatorOnline();
    });

    this.authService.currentUserObs.subscribe(value => {
      this.currentUser = value;
    });
  }

  ngOnDestroy() {
    this.offlineStatusSubscription.unsubscribe();
  }

  forceToggleOffline() {
    this.offline.forceOffline(this.isOnline, OfflineReason.FORCED);

    if (this.offline.isOnline()) {
      this.dialog.open(InitializeLoadScreenComponent, {
        width: '500px',
        maxWidth: '90vw',
        disableClose: true,
        closeOnNavigation: false,
        data: {
          sync: true,
          autoClose: true,
        },
      });
    }
  }

  fetchUsers(searchTerm: string): void {
    this.isLoading = true;
    this.service
      .fetchRawUsers(this.userSearchTerm, this.pageNumber, this.limitPerPage, this.sortField, this.sortOrder)
      .subscribe(result => {
        this.allUsers = result.users;
        // TODO: grab all user images
        this.dataSource = new MatTableDataSource<User>(this.allUsers);
        this.totalPages = result.totalPages;
        this.totalUsers = result.total || result.totalPages * this.limitPerPage;
        this.isLoading = false;
      }, this.handleError.bind(this));
  }

  searchData(searchTerm: string): void {
    this.fetchUsers(searchTerm);
  }

  debounceSortData(sort: Sort) {
    this.debouncedSortData(sort);
  }

  debounceFetchUsers(searchTerm: string) {
    this.debouncedFetchUsers(searchTerm);
  }

  sortData(sort: Sort): void {
    this.pageNumber = 0;
    this.sortField = sort.active ? sort.active : 'lastname';
    this.sortOrder = sort.direction !== '' ? sort.direction : 'asc';
    this.fetchUsers(this.userSearchTerm);
  }

  compareBoolean(a: boolean, b: boolean, isAsc: boolean): number {
    return a === b ? 0 : a ? (isAsc ? 1 : -1) : isAsc ? -1 : 1;
  }

  compare(a: string, b: string, isAsc: boolean): number {
    return a?.toLowerCase().localeCompare(b?.toLowerCase()) * (isAsc ? 1 : -1);
  }

  onPageEvent($event) {
    const pageIndex = $event.pageIndex;
    this.pageNumber = 1 + pageIndex;
    this.fetchUsers(this.userSearchTerm);
  }

  onAddUser() {
    this.dialog
      .open(ManageUserDialogComponent, {
        width: '500px',
        data: null,
      })
      .afterClosed()
      .subscribe(userData => {
        if (userData) {
          this.userManagementService
            .saveUserProfile(userData)
            .subscribe(res => {
              if (res.email_domain_approved) {
                this.authService.approveUserEmail(res.uuid);
              }
              this.fetchUsers(this.userSearchTerm);
            }, this.handleError.bind(this));
        }
      });
  }

  onEditUser(user: User) {
    this.dialog
      .open(ManageUserDialogComponent, {
        width: '500px',
        data: { ...user },
      })
      .afterClosed()
      .subscribe(userData => {
        if (userData) {
          this.userManagementService
            .saveUserProfile(userData, true)
            .subscribe(res => {
              this.fetchUsers(this.userSearchTerm);
            }, this.handleError.bind(this));
        }
      });
  }

  onDeleteUser(user: User) {
    this.dialog
      .open(DeleteConfirmationDialogComponent, {
        width: '500px',
        data: user,
      })
      .afterClosed()
      .subscribe(confirmDelete => {
        if (confirmDelete) {
          this.userManagementService.deleteUser(user).subscribe(res => {
            this.fetchUsers(this.userSearchTerm);
          }, this.handleError.bind(this));
        }
      });
  }

  canAccessRolePermManagement() {
    const permissions: any[] = flatMap(
      this.currentUser.roles.map(r => r.permissions),
    );
    return permissions
      .map(p => p.uuid)
      .includes(environment.rolePermManagementPermissionUUID);
  }

  announceUserApproved(user) {
    if (user) {
      const message = 'The user account was approved: ' + user.firstname + ' ' + user.lastname + ' - ' + user.email;
      this.snackbar.open(message, 'Dismiss');
    }
    this.fetchUsers(this.userSearchTerm);
  }

  announceUserDenied(user) {
    if (user) {
      const message = 'The user account was denied: ' + user.firstname + ' ' + user.lastname + ' - ' + user.email;
      this.snackbar.open(message, 'Dismiss');
    }
    this.fetchUsers(this.userSearchTerm);
  }

  handleError(error) {
    this.snackbar.open(error.error.message, 'Dismiss');
  }
}

@Injectable()
export class CustomPaginator extends MatPaginatorIntl {
  getRangeLabel = (page, pageSize, length): string => {
    const maxPages = Math.ceil(length / pageSize);
    const currentPage = maxPages > 0 ? page + 1 : 0;
    return `Page ${currentPage} of ${maxPages}`;
  }
}
